/**
 * First-party feature flags.
 *
 * Beta gating without a third-party tracker (see the PostHog NO-GO decision,
 * `.planning/research/posthog-go-no-go-2026-06.md`). Two shapes, one table:
 *   - kill-switch a shipped surface       → flag.enabled = false
 *   - gate a feature to specific testers  → audience='allowlist' + allowed_user_ids
 *
 * Evaluation goes through the `enabled_features()` RPC (SECURITY DEFINER) which
 * returns ONLY the keys enabled for the calling user — clients never see the
 * allowlist. Flag writes (admin screen) hit the table directly under admin RLS.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured, useAuth } from '@/lib/supabase';
import { rpc } from '@/lib/supabase/rpc';
import { mode } from '@/lib/config/app-mode';
import { logAdminAction } from '@/lib/admin/log-admin-action';
import { reportError } from '@/lib/errors';
import type { Database } from '@/lib/supabase/types';
import { resolveFlag } from './resolve';
import { resolveFlagRouteAccess, type FlagRouteAccess } from './route-access';

export { resolveFlag } from './resolve';
export { resolveFlagRouteAccess, type FlagRouteAccess } from './route-access';

export type FeatureFlag = Database['public']['Tables']['feature_flags']['Row'];
export type FeatureFlagUpsert = Database['public']['Tables']['feature_flags']['Insert'];

export const featureFlagKeys = {
  enabled: (userId: string) => ['feature-flags', 'enabled', userId] as const,
  all: () => ['feature-flags', 'all'] as const,
};

/**
 * The set of flag keys enabled for the current user. Keyed by user id so it never
 * bleeds across a sign-out → sign-in into a different account. Inert in mock mode.
 */
export function useEnabledFeatures() {
  const { user } = useAuth();
  const userId = user?.id;
  return useQuery({
    queryKey: featureFlagKeys.enabled(userId || 'anon'),
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await rpc('enabled_features');
      if (error) throw error;
      return data ?? [];
    },
    enabled: !mode.isMock && !!userId && isSupabaseConfigured(),
    staleTime: 60 * 1000, // 1 min — flags change rarely; a kill-switch propagates within a minute
  });
}

/** Resolve a single flag to a boolean. See {@link resolveFlag} for the default semantics. */
export function useFlag(key: string, { default: defaultValue }: { default: boolean }): boolean {
  const { data, isSuccess } = useEnabledFeatures();
  return resolveFlag({ key, defaultValue, isMock: mode.isMock, isSuccess, enabledKeys: data });
}

/**
 * Whether a flag-gated ROUTE may render, wait, or must redirect away.
 *
 * Use this — never {@link useFlag} — when the answer decides NAVIGATION. `useFlag` returns
 * the caller's default while the flag set loads, which for a redirect means bouncing a user
 * who holds the flag on the first render of every cold navigation, and bouncing every
 * mock-mode user forever. See {@link resolveFlagRouteAccess} for the full reasoning.
 *
 * The caller must render something neutral for 'loading' and only act on 'redirect'.
 */
export function useFlagRouteAccess(key: string): FlagRouteAccess {
  const { data, isSuccess, isLoading, isError } = useEnabledFeatures();
  return resolveFlagRouteAccess({
    key,
    isMock: mode.isMock,
    isLoading,
    isError,
    isSuccess,
    enabledKeys: data,
  });
}

// ============================================
// Admin management (admin RLS gates the table)
// ============================================

/** All flags, for the admin management screen. */
export function useAllFeatureFlags() {
  return useQuery({
    queryKey: featureFlagKeys.all(),
    queryFn: async (): Promise<FeatureFlag[]> => {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .order('key', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: isSupabaseConfigured(),
    staleTime: 30 * 1000,
  });
}

/** Create or update a flag (admin). Invalidates both the admin list and eval caches. */
export function useUpsertFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (flag: FeatureFlagUpsert): Promise<FeatureFlag> => {
      const { data, error } = await supabase
        .from('feature_flags')
        .upsert(flag, { onConflict: 'key' })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (saved) => {
      // A flag toggle changes behaviour for real users, so it belongs in the
      // audit feed with enough context to answer "who turned this on, when".
      void logAdminAction({
        action: 'feature_flag_updated',
        targetType: 'feature_flag',
        targetId: saved.key,
        details: { key: saved.key, enabled: saved.enabled },
      });
      // Refresh the admin list and every user's evaluated set.
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
    },
    onError: (err) => reportError(err, { context: 'useUpsertFeatureFlag', level: 'warning', silent: true }),
  });
}
